import {
	type AuthCodeData,
	type ClientMetadata,
	type OAuthStorage,
  type LexiconPermissionSet,
	type PARData,
	type TokenData,
	REFRESH_TOKEN_TTL,
} from "@getcirrus/oauth-provider";

/**
 * A cached permission-set lookup. `stale` means the entry has passed its
 * 24h soft-expiry: it is still safe to use but should be refreshed
 * opportunistically.
 */
export interface CachedPermissionSet {
	set: LexiconPermissionSet;
	fetchedAt: number;
	stale: boolean;
}

/**
 * SQLite-backed OAuth storage for Cloudflare Durable Objects.
 *
 * Implements the OAuthStorage interface from @getcirrus/oauth-provider,
 * storing OAuth data in SQLite tables within a Durable Object.
 */
export class SqliteOAuthStorage implements OAuthStorage {
	constructor(private sql: SqlStorage) {}

	/**
	 * Initialize the OAuth database schema. Should be called once on DO startup.
	 */
	initSchema(): void {
		this.sql.exec(`
			CREATE TABLE IF NOT EXISTS oauth_auth_codes (
				code TEXT PRIMARY KEY,
				client_id TEXT NOT NULL,
				redirect_uri TEXT NOT NULL,
				code_challenge TEXT NOT NULL,
				code_challenge_method TEXT NOT NULL DEFAULT 'S256',
				scope TEXT NOT NULL,
				sub TEXT NOT NULL,
				expires_at INTEGER NOT NULL
			);

			CREATE INDEX IF NOT EXISTS idx_auth_codes_expires ON oauth_auth_codes(expires_at);

			CREATE TABLE IF NOT EXISTS oauth_tokens (
				access_token TEXT PRIMARY KEY,
				refresh_token TEXT NOT NULL UNIQUE,
				client_id TEXT NOT NULL,
				sub TEXT NOT NULL,
				scope TEXT NOT NULL,
				dpop_jkt TEXT,
				issued_at INTEGER NOT NULL,
				access_expires_at INTEGER NOT NULL,
				refresh_expires_at INTEGER NOT NULL,
				revoked INTEGER NOT NULL DEFAULT 0
			);


			-- Cached client metadata
			CREATE TABLE IF NOT EXISTS oauth_clients (
				client_id TEXT PRIMARY KEY,
				client_name TEXT NOT NULL,
				redirect_uris TEXT NOT NULL,
				logo_uri TEXT,
				client_uri TEXT,
				token_endpoint_auth_method TEXT NOT NULL DEFAULT 'none',
				jwks TEXT,
				jwks_uri TEXT,
				cached_at INTEGER NOT NULL
			);

			CREATE TABLE IF NOT EXISTS oauth_par_requests (
				request_uri TEXT PRIMARY KEY,
				client_id TEXT NOT NULL,
				params TEXT NOT NULL,
				expires_at INTEGER NOT NULL
			);

			CREATE INDEX IF NOT EXISTS idx_par_expires ON oauth_par_requests(expires_at);

			CREATE TABLE IF NOT EXISTS oauth_nonces (
				nonce TEXT PRIMARY KEY,
				created_at INTEGER NOT NULL
			);

			CREATE INDEX IF NOT EXISTS idx_nonces_created ON oauth_nonces(created_at);

			CREATE TABLE IF NOT EXISTS oauth_webauthn_challenges (
				challenge TEXT PRIMARY KEY,
				created_at INTEGER NOT NULL
			);

			CREATE INDEX IF NOT EXISTS idx_challenges_created ON oauth_webauthn_challenges(created_at);

			-- Cached permission-set lexicons resolved via @atcute/lexicon-resolver.
			-- Cache is per-account (lives inside this AccountDurableObject) and
			-- uses the stale-while-revalidate semantics from the atproto
			-- permission spec.
			CREATE TABLE IF NOT EXISTS oauth_permission_sets (
				nsid TEXT PRIMARY KEY,
				lexicon TEXT NOT NULL,
				fetched_at INTEGER NOT NULL,
				stale_at INTEGER NOT NULL,
				expires_at INTEGER NOT NULL
			);

			CREATE INDEX IF NOT EXISTS idx_permission_sets_expires ON oauth_permission_sets(expires_at);
		`);

		// Migrations for older OAuth schema versions
		this.migrateClientTable();
		this.migrateTokensTable();
		this.ensureTokenIndexes();
	}

	private migrateClientTable(): void {
		const columns = this.sql
			.exec("PRAGMA table_info(oauth_clients)")
			.toArray()
			.map((r) => r.name as string);

		if (!columns.includes("token_endpoint_auth_method")) {
			this.sql.exec(
				"ALTER TABLE oauth_clients ADD COLUMN token_endpoint_auth_method TEXT NOT NULL DEFAULT 'none'",
			);
			this.sql.exec("ALTER TABLE oauth_clients ADD COLUMN jwks TEXT");
			this.sql.exec("ALTER TABLE oauth_clients ADD COLUMN jwks_uri TEXT");
			this.sql.exec("DELETE FROM oauth_clients");
		}
	}

	private migrateTokensTable(): void {
		const columns = this.sql
			.exec("PRAGMA table_info(oauth_tokens)")
			.toArray()
			.map((r) => r.name as string);

		if (columns.length === 0) return;
		if (
			columns.includes("access_expires_at") &&
			columns.includes("refresh_expires_at")
		) {
			return;
		}

		this.sql.exec(`
			CREATE TABLE oauth_tokens_new (
				access_token TEXT PRIMARY KEY,
				refresh_token TEXT NOT NULL UNIQUE,
				client_id TEXT NOT NULL,
				sub TEXT NOT NULL,
				scope TEXT NOT NULL,
				dpop_jkt TEXT,
				issued_at INTEGER NOT NULL,
				access_expires_at INTEGER NOT NULL,
				refresh_expires_at INTEGER NOT NULL,
				revoked INTEGER NOT NULL DEFAULT 0
			);
		`);

		this.sql.exec(
			`INSERT INTO oauth_tokens_new (
				access_token, refresh_token, client_id, sub, scope, dpop_jkt, issued_at, access_expires_at, refresh_expires_at, revoked
			)
			SELECT
				access_token, refresh_token, client_id, sub, scope, dpop_jkt, issued_at,
				expires_at AS access_expires_at,
				MAX(expires_at, issued_at + ?) AS refresh_expires_at,
				revoked
			FROM oauth_tokens`,
			REFRESH_TOKEN_TTL,
		);

		this.sql.exec("DROP TABLE oauth_tokens");
		this.sql.exec("ALTER TABLE oauth_tokens_new RENAME TO oauth_tokens");
	}

	private ensureTokenIndexes(): void {
		this.sql.exec(
			"CREATE INDEX IF NOT EXISTS idx_tokens_refresh ON oauth_tokens(refresh_token)",
		);
		this.sql.exec(
			"CREATE INDEX IF NOT EXISTS idx_tokens_sub ON oauth_tokens(sub)",
		);
		this.sql.exec(
			"CREATE INDEX IF NOT EXISTS idx_tokens_access_expires ON oauth_tokens(access_expires_at)",
		);
		this.sql.exec(
			"CREATE INDEX IF NOT EXISTS idx_tokens_refresh_expires ON oauth_tokens(refresh_expires_at)",
		);
	}

	/**
	 * Clean up expired entries. Should be called periodically.
	 */
	cleanup(): void {
		const now = Date.now();
		this.sql.exec("DELETE FROM oauth_auth_codes WHERE expires_at < ?", now);
		this.sql.exec(
			"DELETE FROM oauth_tokens WHERE refresh_expires_at < ?",
			now,
		);
		this.sql.exec("DELETE FROM oauth_par_requests WHERE expires_at < ?", now);
		this.sql.exec(
			"DELETE FROM oauth_permission_sets WHERE expires_at < ?",
			now,
		);
		// Nonces expire after 5 minutes
		const nonceExpiry = now - 5 * 60 * 1000;
		this.sql.exec("DELETE FROM oauth_nonces WHERE created_at < ?", nonceExpiry);
		// WebAuthn challenges expire after 2 minutes
		const challengeExpiry = now - 2 * 60 * 1000;
		this.sql.exec(
			"DELETE FROM oauth_webauthn_challenges WHERE created_at < ?",
			challengeExpiry,
		);
	}

	// ============================================
	// Authorization Codes
	// ============================================

	async saveAuthCode(code: string, data: AuthCodeData): Promise<void> {
		this.sql.exec(
			`INSERT INTO oauth_auth_codes
			(code, client_id, redirect_uri, code_challenge, code_challenge_method, scope, sub, expires_at)
			VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
			code,
			data.clientId,
			data.redirectUri,
			data.codeChallenge,
			data.codeChallengeMethod,
			data.scope,
			data.sub,
			data.expiresAt,
		);
	}

	async getAuthCode(code: string): Promise<AuthCodeData | null> {
		const rows = this.sql
			.exec(
				`SELECT client_id, redirect_uri, code_challenge, code_challenge_method, scope, sub, expires_at
				FROM oauth_auth_codes WHERE code = ?`,
				code,
			)
			.toArray();

		if (rows.length === 0) return null;

		const row = rows[0]!;
		const expiresAt = row.expires_at as number;

		if (Date.now() > expiresAt) {
			this.sql.exec("DELETE FROM oauth_auth_codes WHERE code = ?", code);
			return null;
		}

		return {
			clientId: row.client_id as string,
			redirectUri: row.redirect_uri as string,
			codeChallenge: row.code_challenge as string,
			codeChallengeMethod: row.code_challenge_method as "S256",
			scope: row.scope as string,
			sub: row.sub as string,
			expiresAt,
		};
	}

	async deleteAuthCode(code: string): Promise<void> {
		this.sql.exec("DELETE FROM oauth_auth_codes WHERE code = ?", code);
	}

	// ============================================
	// Tokens
	// ============================================

	async saveTokens(data: TokenData): Promise<void> {
		this.sql.exec(
			`INSERT INTO oauth_tokens
			(access_token, refresh_token, client_id, sub, scope, dpop_jkt, issued_at, access_expires_at, refresh_expires_at, revoked)
			VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
			data.accessToken,
			data.refreshToken,
			data.clientId,
			data.sub,
			data.scope,
			data.dpopJkt ?? null,
			data.issuedAt,
			data.accessExpiresAt,
			data.refreshExpiresAt,
			data.revoked ? 1 : 0,
		);
	}

	async getTokenByAccess(accessToken: string): Promise<TokenData | null> {
		const rows = this.sql
			.exec(
				`SELECT access_token, refresh_token, client_id, sub, scope, dpop_jkt, issued_at, access_expires_at, refresh_expires_at, revoked
				FROM oauth_tokens WHERE access_token = ?`,
				accessToken,
			)
			.toArray();

		if (rows.length === 0) return null;

		const row = rows[0]!;
		const revoked = Boolean(row.revoked);
		const accessExpiresAt = row.access_expires_at as number;

		if (revoked || Date.now() > accessExpiresAt) {
			return null;
		}

		return {
			accessToken: row.access_token as string,
			refreshToken: row.refresh_token as string,
			clientId: row.client_id as string,
			sub: row.sub as string,
			scope: row.scope as string,
			dpopJkt: (row.dpop_jkt as string) ?? undefined,
			issuedAt: row.issued_at as number,
			accessExpiresAt,
			refreshExpiresAt: row.refresh_expires_at as number,
			revoked,
		};
	}

	async getTokenByRefresh(refreshToken: string): Promise<TokenData | null> {
		const rows = this.sql
			.exec(
				`SELECT access_token, refresh_token, client_id, sub, scope, dpop_jkt, issued_at, access_expires_at, refresh_expires_at, revoked
				FROM oauth_tokens WHERE refresh_token = ?`,
				refreshToken,
			)
			.toArray();

		if (rows.length === 0) return null;

		const row = rows[0]!;
		const revoked = Boolean(row.revoked);

		const refreshExpiresAt = row.refresh_expires_at as number;

		if (revoked || Date.now() > refreshExpiresAt) return null;

		return {
			accessToken: row.access_token as string,
			refreshToken: row.refresh_token as string,
			clientId: row.client_id as string,
			sub: row.sub as string,
			scope: row.scope as string,
			dpopJkt: (row.dpop_jkt as string) ?? undefined,
			issuedAt: row.issued_at as number,
			accessExpiresAt: row.access_expires_at as number,
			refreshExpiresAt,
			revoked,
		};
	}

	async revokeToken(accessToken: string): Promise<void> {
		this.sql.exec(
			"UPDATE oauth_tokens SET revoked = 1 WHERE access_token = ?",
			accessToken,
		);
	}

	async revokeAllTokens(sub: string): Promise<void> {
		this.sql.exec("UPDATE oauth_tokens SET revoked = 1 WHERE sub = ?", sub);
	}

	// ============================================
	// Clients
	// ============================================

	async saveClient(clientId: string, metadata: ClientMetadata): Promise<void> {
		this.sql.exec(
			`INSERT OR REPLACE INTO oauth_clients
			(client_id, client_name, redirect_uris, logo_uri, client_uri, token_endpoint_auth_method, jwks, jwks_uri, cached_at)
			VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
			clientId,
			metadata.clientName,
			JSON.stringify(metadata.redirectUris),
			metadata.logoUri ?? null,
			metadata.clientUri ?? null,
			metadata.tokenEndpointAuthMethod ?? "none",
			metadata.jwks ? JSON.stringify(metadata.jwks) : null,
			metadata.jwksUri ?? null,
			metadata.cachedAt ?? Date.now(),
		);
	}

	async getClient(clientId: string): Promise<ClientMetadata | null> {
		const rows = this.sql
			.exec(
				`SELECT client_id, client_name, redirect_uris, logo_uri, client_uri, token_endpoint_auth_method, jwks, jwks_uri, cached_at
				FROM oauth_clients WHERE client_id = ?`,
				clientId,
			)
			.toArray();

		if (rows.length === 0) return null;

		const row = rows[0]!;
		return {
			clientId: row.client_id as string,
			clientName: row.client_name as string,
			redirectUris: JSON.parse(row.redirect_uris as string) as string[],
			logoUri: (row.logo_uri as string) ?? undefined,
			clientUri: (row.client_uri as string) ?? undefined,
			tokenEndpointAuthMethod:
				(row.token_endpoint_auth_method as "none" | "private_key_jwt") ??
				"none",
			jwks: row.jwks
				? (JSON.parse(row.jwks as string) as ClientMetadata["jwks"])
				: undefined,
			jwksUri: (row.jwks_uri as string) ?? undefined,
			cachedAt: row.cached_at as number,
		};
	}

	// ============================================
	// PAR Requests
	// ============================================

	async savePAR(requestUri: string, data: PARData): Promise<void> {
		this.sql.exec(
			`INSERT INTO oauth_par_requests (request_uri, client_id, params, expires_at)
			VALUES (?, ?, ?, ?)`,
			requestUri,
			data.clientId,
			JSON.stringify(data.params),
			data.expiresAt,
		);
	}

	async getPAR(requestUri: string): Promise<PARData | null> {
		const rows = this.sql
			.exec(
				`SELECT client_id, params, expires_at FROM oauth_par_requests WHERE request_uri = ?`,
				requestUri,
			)
			.toArray();

		if (rows.length === 0) return null;

		const row = rows[0]!;
		const expiresAt = row.expires_at as number;

		if (Date.now() > expiresAt) {
			this.sql.exec(
				"DELETE FROM oauth_par_requests WHERE request_uri = ?",
				requestUri,
			);
			return null;
		}

		return {
			clientId: row.client_id as string,
			params: JSON.parse(row.params as string) as Record<string, string>,
			expiresAt,
		};
	}

	async deletePAR(requestUri: string): Promise<void> {
		this.sql.exec(
			"DELETE FROM oauth_par_requests WHERE request_uri = ?",
			requestUri,
		);
	}

	// ============================================
	// DPoP Nonces
	// ============================================

	async checkAndSaveNonce(nonce: string): Promise<boolean> {
		// Check if nonce already exists
		const rows = this.sql
			.exec("SELECT 1 FROM oauth_nonces WHERE nonce = ? LIMIT 1", nonce)
			.toArray();

		if (rows.length > 0) {
			return false; // Nonce already used
		}

		// Save the nonce
		this.sql.exec(
			"INSERT INTO oauth_nonces (nonce, created_at) VALUES (?, ?)",
			nonce,
			Date.now(),
		);

		return true;
	}

	/**
	 * Clear all OAuth data (for testing).
	 */
	destroy(): void {
		this.sql.exec("DELETE FROM oauth_auth_codes");
		this.sql.exec("DELETE FROM oauth_tokens");
		this.sql.exec("DELETE FROM oauth_clients");
		this.sql.exec("DELETE FROM oauth_par_requests");
		this.sql.exec("DELETE FROM oauth_nonces");
		this.sql.exec("DELETE FROM oauth_webauthn_challenges");
		this.sql.exec("DELETE FROM oauth_permission_sets");
	}

	// ============================================
	// WebAuthn Challenges
	// ============================================

	// ============================================
	// Permission-set cache
	// ============================================

	/** Soft-expiry: serve stale, but try to refresh. */
	static readonly PERMISSION_SET_STALE_MS = 24 * 60 * 60 * 1000;
	/** Hard-expiry: drop entirely. */
	static readonly PERMISSION_SET_EXPIRY_MS = 90 * 24 * 60 * 60 * 1000;

	savePermissionSet(
		nsid: string,
		set: LexiconPermissionSet,
		now: number = Date.now(),
	): void {
		this.sql.exec(
			`INSERT OR REPLACE INTO oauth_permission_sets
			(nsid, lexicon, fetched_at, stale_at, expires_at)
			VALUES (?, ?, ?, ?, ?)`,
			nsid,
			JSON.stringify(set),
			now,
			now + SqliteOAuthStorage.PERMISSION_SET_STALE_MS,
			now + SqliteOAuthStorage.PERMISSION_SET_EXPIRY_MS,
		);
	}

	getPermissionSet(nsid: string): CachedPermissionSet | null {
		const rows = this.sql
			.exec(
				"SELECT lexicon, fetched_at, stale_at, expires_at FROM oauth_permission_sets WHERE nsid = ?",
				nsid,
			)
			.toArray();
		if (rows.length === 0) return null;
		const row = rows[0]!;
		const now = Date.now();
		if (now > (row.expires_at as number)) {
			this.sql.exec("DELETE FROM oauth_permission_sets WHERE nsid = ?", nsid);
			return null;
		}
		return {
			set: JSON.parse(row.lexicon as string) as LexiconPermissionSet,
			fetchedAt: row.fetched_at as number,
			stale: now > (row.stale_at as number),
		};
	}

	/**
	 * Save a WebAuthn challenge for later verification
	 */
	saveWebAuthnChallenge(challenge: string): void {
		this.sql.exec(
			"INSERT INTO oauth_webauthn_challenges (challenge, created_at) VALUES (?, ?)",
			challenge,
			Date.now(),
		);
	}

	/**
	 * Consume a WebAuthn challenge (single-use, deleted after retrieval)
	 * @returns true if challenge was valid and consumed, false if not found or expired
	 */
	consumeWebAuthnChallenge(challenge: string): boolean {
		// Check if challenge exists and is not expired (2 min TTL)
		const twoMinutesAgo = Date.now() - 2 * 60 * 1000;
		const rows = this.sql
			.exec(
				"SELECT challenge FROM oauth_webauthn_challenges WHERE challenge = ? AND created_at > ?",
				challenge,
				twoMinutesAgo,
			)
			.toArray();

		if (rows.length === 0) {
			return false;
		}

		// Delete the challenge (single-use)
		this.sql.exec(
			"DELETE FROM oauth_webauthn_challenges WHERE challenge = ?",
			challenge,
		);

		return true;
	}
}
